import type { PayloadAction } from '@reduxjs/toolkit';
import { logger } from 'lib/logger';
import { isEmpty } from 'ramda';
import { BillingPersonalizationAPICall } from 'reactApp/api/billing/personalization/BillingPersonalizationAPICall';
import type { BillingPersonalizationResponse } from 'reactApp/api/billing/personalization/BillingPersonalizationAPICall.types';
import { BillingPersonalizationDataAPICall } from 'reactApp/api/billing/personalization/BillingPersonalizationDataAPICall';
import { IS_IOS_MOBILE } from 'reactApp/appHelpers/configHelpers';
import {
    billingPersonalizationDataSource,
    billingPersonalizationUseDataApi,
} from 'reactApp/appHelpers/featuresHelpers/features/billingPersonalization';
import { getQueryParams } from 'reactApp/appHelpers/settingsHelpers';
import {
    billingPersonalizationLoadFailure,
    billingPersonalizationLoadRequest,
    billingPersonalizationLoadSuccess,
    initPersonalTariffs,
} from 'reactApp/modules/billingPersonalization/billingPersonalization.module';
import { BillingPersonalizationTypeToTabName } from 'reactApp/modules/billingPersonalization/billingPersonalization.types';
import { setMedia } from 'reactApp/modules/media/media.actions';
import { mediaMatch } from 'reactApp/modules/media/media.helpers';
import {
    changeMultiTariffActiveTabAction,
    changeMultiTariffMobileActiveTabAction,
} from 'reactApp/modules/quotaLanding/quotaLanding.module';
import { QuotaLandingSelectors } from 'reactApp/modules/quotaLanding/quotaLanding.selector';
import { sendXray } from 'reactApp/utils/ga';
import { captureException } from 'reactApp/utils/tracer';
import { take, takeEvery } from 'redux-saga/effects';
import { call, put, select } from 'typed-redux-saga';

// eslint-disable-next-line unused-imports/no-unused-vars
const billingPersonalizationApiCall = () => {
    const { useDataApi: useDataApiFromQuery } = getQueryParams() || {};
    const ApiCall =
        billingPersonalizationUseDataApi || useDataApiFromQuery === 'true'
            ? BillingPersonalizationDataAPICall
            : BillingPersonalizationAPICall;
    return new ApiCall().makeRequest();
};

function* handleBillingPersonalizationRequestSuccess(action: PayloadAction<BillingPersonalizationResponse>) {
    const data = action.payload[billingPersonalizationDataSource];

    mediaMatch();
    yield take(setMedia);
    const isMobile = yield* select(QuotaLandingSelectors.isMobile);

    // Выставляем дефолтную линейку тарифов по данным из апи. Мобилку не разделяем на тач/ вебвью. Разделяем только по платформам.
    if (isMobile) {
        const type = IS_IOS_MOBILE ? data?.promo_quota_ios?.default?.type : data?.promo_quota_android?.default?.type;
        if (type) {
            yield put(changeMultiTariffMobileActiveTabAction(BillingPersonalizationTypeToTabName[type]));
        }
    } else {
        const type = data?.promo_quota_web?.default?.type;
        if (type) {
            yield put(changeMultiTariffActiveTabAction(BillingPersonalizationTypeToTabName[type]));
        }
    }
}

export function* handleBillingPersonalizationRequest() {
    try {
        sendXray(['billingPersonalization', 'request', 'start']);

        const apiData: { data: BillingPersonalizationResponse } = yield call(billingPersonalizationApiCall);
        const { data: response } = apiData || {};

        if (!response || isEmpty(response.data1)) {
            sendXray(['billingPersonalization', 'request', 'empty', 'error']);
            logger.error(`no billing personalization data from api`);
            captureException(`no billing personalization data from api`, { issueKey: `billingPersonalization/request/empty` });
            yield put(billingPersonalizationLoadFailure());
            return;
        }

        sendXray(['billingPersonalization', 'request', 'success']);
        yield put(billingPersonalizationLoadSuccess(response));
    } catch (error) {
        sendXray(['billingPersonalization', 'request', 'fail']);
        logger.error(error);
        captureException(error, { issueKey: `billingPersonalization/request/fail` });
        yield put(billingPersonalizationLoadFailure());
    }
}

export function* handleInitPersonalTariffs() {
    yield call(handleBillingPersonalizationRequest);
}

export function* watchBillingPersonalization() {
    yield takeEvery(billingPersonalizationLoadRequest.toString(), handleBillingPersonalizationRequest);
    yield takeEvery(billingPersonalizationLoadSuccess.toString(), handleBillingPersonalizationRequestSuccess);
    yield takeEvery(initPersonalTariffs.toString(), handleInitPersonalTariffs);
}
